'use client';
import classNames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FC, useEffect, useRef } from 'react';

import { useLogout } from '@/features/auth';
import { useUserStore } from '@/features/user';
import { SITE_PAGES } from '@/shared/config';
import { Icon } from '@/shared/ui';

import styles from './isUserTooltip.module.scss';

interface isUserTooltip {
  className?: string;
  onClose: () => void;
  isActive: boolean;
}

const IsUserTooltip: FC<isUserTooltip> = (props) => {
  const { className, onClose, isActive } = props;
  const { user } = useUserStore();
  const { logout, error } = useLogout();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  // Закрываем tooltip при изменении роута
  useEffect(() => {
    if (isActive) {
      onClose();
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        isActive
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, isActive]);

  function handleLogout() {
    void logout();
  }

  return (
    <div ref={tooltipRef} className={classNames(styles.tooltip, className)}>
      <div className={styles.tooltip__wrapper}>
        <ul className={styles.tooltip__user_list}>
          <li className={styles.tooltip__list_item}>
            <Link
              className={styles.tooltip__link}
              href={SITE_PAGES.accountSettings}
            >
              <p className={styles.tooltip__text}>
                {user?.first_name} {user?.last_name}
              </p>
            </Link>
          </li>
          <li className={styles.tooltip__list_item}>
            <p
              className={classNames(
                styles.tooltip__text,
                user?.email_verified_at
                  ? styles['tooltip__text--grey']
                  : styles['tooltip__text--red'],
              )}
            >
              {user?.email}
            </p>
            {!user?.email_verified_at && (
              <Icon className={styles.tooltip__list_icon} name="info" />
            )}
          </li>
          {user?.personal_discount_percent && (
            <li
              className={classNames(
                styles.tooltip__list_item,
                styles['tooltip__list_item--discount'],
              )}
            >
              <p className={classNames(styles.tooltip__text)}>
                Моя персональная скидка - {user?.personal_discount_percent}%
              </p>
            </li>
          )}
        </ul>
        <ul>
          <li className={styles.tooltip__list_item}>
            <Link
              className={styles.tooltip__link}
              href={SITE_PAGES.accountOrders}
            >
              <span className={styles.tooltip__text}>Мои заказы</span>
              {user?.order_count && (
                <span className={styles.tooltip__text}>
                  {user?.order_count}
                </span>
              )}
            </Link>
          </li>
        </ul>
        {error && <p role="alert">{error}</p>}
        <button
          className={classNames(styles.tooltip__btn)}
          type="button"
          onClick={handleLogout}
        >
          <Icon name="leave" />
          <p className={styles.tooltip__text}>Выйти</p>
        </button>
      </div>
    </div>
  );
};

export default IsUserTooltip;
